import { useMemo } from "react";
import { DEFAULT_REPORT_AMOUNT_UNIT } from "../../../../api/tenantInternalReports";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import InternalReportTable, {
  ReportTableRow,
} from "../../../../components/ui/reportTable";
import { formatFyLabel, fyToDateWindow, priorFyKey } from "../../../../lib/utils";
import {
  selectFraudByFicCategoryReportName,
  selectFraudByFicCategoryRows,
  selectFraudByFicCategorySelectedFy,
  selectFraudByFicCategoryStatus,
  selectFraudByFicCategoryTotals,
} from "../../logic/fraudByFicCategoryReportSlice";
import { fetchFraudByFicCategoryReportThunk } from "../../logic/fraudByFicCategoryReportThunk";
import FinancialYearPicker from "../../../../components/ui/calendar/FinancialYearPicker";
import InternalReportMetaHeader from "../common/InternalReportMetaHeader";
import InternalReportTableShimmer from "../common/InternalReportTableShimmer";
import {
  buildFraudByFicCategoryColumns,
  buildFraudByFicCategoryMergedHeadersSets,
  FraudByFicCategoryTableRow,
} from "./columns";

const FraudByFicCategoryReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const categories = useAppSelector(selectFraudByFicCategoryRows);
  const totals = useAppSelector(selectFraudByFicCategoryTotals);
  const reportName = useAppSelector(selectFraudByFicCategoryReportName);
  const selectedFy = useAppSelector(selectFraudByFicCategorySelectedFy);
  const isFetching = useAppSelector(selectFraudByFicCategoryStatus) === "loading";

  const previousFy = useMemo(
    () => (selectedFy ? priorFyKey(selectedFy) : null),
    [selectedFy]
  );

  const windows = useMemo(
    () =>
      selectedFy && previousFy
        ? {
            current: fyToDateWindow(selectedFy),
            previous: fyToDateWindow(previousFy),
          }
        : null,
    [selectedFy, previousFy]
  );

  const overviewRows = useMemo<ReportTableRow<FraudByFicCategoryTableRow>[]>(() => {
    const data_rows: ReportTableRow<FraudByFicCategoryTableRow>[] =
      categories.map((row, index) => ({
        key: row.category,
        data: {
          serial_number: `${index + 1}.`,
          category: row.category,
          data_by_date_windows: row.data_by_date_windows,
        },
        rowCellClassName: `border-b ${
          index === categories.length - 1
            ? "border-b-[#969696]"
            : "border-b-[#E5E5E5]"
        }`,
      }));

    const totals_row: ReportTableRow<FraudByFicCategoryTableRow> = {
      key: "total",
      data: {
        serial_number: "",
        category: "Total",
        data_by_date_windows: totals,
      },
      rowCellClassName:
        "border-t border-b border-t-[#969696] border-b-[#969696] font-bold",
    };

    return [...data_rows, totals_row];
  }, [categories, totals]);

  if (!selectedFy || !previousFy || !windows) return null;

  const handleFyChange = (fy: string) =>
    dispatch(fetchFraudByFicCategoryReportThunk({ fy }));

  const metaItems = [
    { label: "Report Name", value: reportName ?? "-" },
    {
      label: "Periods Compared",
      value: `${formatFyLabel(selectedFy)} vs ${formatFyLabel(previousFy)}`,
    },
    { label: "Amount Unit", value: DEFAULT_REPORT_AMOUNT_UNIT.label },
  ];

  const overviewColumns = buildFraudByFicCategoryColumns(
    windows.current,
    windows.previous
  );

  const mergedHeadersSets = buildFraudByFicCategoryMergedHeadersSets(
    formatFyLabel(selectedFy),
    formatFyLabel(previousFy)
  );

  return (
    <div className="flex w-full flex-col gap-7">
      <InternalReportMetaHeader items={metaItems} />

      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full items-center justify-between">
          <p className="text-base font-semibold leading-6 text-[#374151]">
            Fraud Category Breakdown
          </p>
          <FinancialYearPicker value={selectedFy} onChange={handleFyChange} />
        </div>
        {isFetching ? (
          <InternalReportTableShimmer columns={6} rows={6} />
        ) : (
          <InternalReportTable<FraudByFicCategoryTableRow>
            columns={overviewColumns}
            mergedHeadersSets={mergedHeadersSets}
            rows={overviewRows}
          />
        )}
      </div>
    </div>
  );
};

export default FraudByFicCategoryReport;
